import { createStore } from "../store/createStore";
import { deepMerge, deepMergeReplacingArrays } from "../utils/objects";
import { type DeepPartial } from "../types/helpers";
import { type IdCheckLoopServiceType } from "../types/runtimeTypes";
import {
  type AllResponseTypes,
  type ConsumerInfoState,
  type ErrorDetailId,
  type ErrorId,
  type Locale,
  type MockCaptchaState,
  type MockStep,
  type ProgramTheme,
  type Segment,
  type SubSegment,
  type VerificationStep,
} from "../types/types";

/** Preview mock directives, mirroring the `mock*` fields on `Options`. */
export interface MockOverrides {
  step?: MockStep;
  previousStep?: VerificationStep;
  segment?: Segment;
  subSegment?: SubSegment;
  errorId?: ErrorId;
  errorDetailId?: ErrorDetailId;
  captchaState?: MockCaptchaState;
  rewardCode?: string;
  redirectUrl?: string;
  consumerInfoState?: ConsumerInfoState;
  idCheckLoopServiceType?: IdCheckLoopServiceType;
  docSelected?: string;
  thirdPartyProvider?: string;
  doFetchTheme?: boolean;
}

/** Preview edits layered on top of the baseline. */
export interface PreviewOverrides {
  programTheme?: DeepPartial<ProgramTheme>;
  response?: Partial<AllResponseTypes>;
  mock?: MockOverrides;
  messages?: Partial<Record<Locale, Record<string, unknown>>>;
}

export interface ProgramStoreState {
  /** The fetched program theme; `undefined` until loaded. */
  baseline: ProgramTheme | undefined;
  overrides: PreviewOverrides;
}

export const programStore = createStore<ProgramStoreState>({
  baseline: undefined,
  overrides: {},
});

export function setProgramBaseline(baseline: ProgramTheme): void {
  programStore.set((prev) => ({ ...prev, baseline }));
}

/**
 * Merge a (partial) theme into the baseline. A partial merge onto an absent baseline replaces it.
 * Uses `deepMerge` to stay byte-identical to the Redux `PROGRAM_THEME` reducer (the legacy `setProgramTheme` dual-writes both, and they're read in parallel so they must not diverge)
 */
export function mergeProgramBaseline(
  patch: DeepPartial<ProgramTheme> | ProgramTheme,
  partial = true,
): void {
  programStore.set((prev) =>
    !partial || !prev.baseline
      ? { ...prev, baseline: patch as ProgramTheme }
      : { ...prev, baseline: deepMerge({}, prev.baseline, patch) as ProgramTheme },
  );
}

export function setPreviewOverrides(patch: PreviewOverrides): void {
  programStore.set((prev) => ({
    ...prev,
    overrides: deepMergeReplacingArrays(prev.overrides, patch),
  }));
}

export function clearPreviewOverrides(): void {
  programStore.set((prev) =>
    Object.keys(prev.overrides).length === 0 ? prev : { ...prev, overrides: {} },
  );
}

export const selectBaseline = (s: ProgramStoreState): ProgramTheme | undefined => s.baseline;

// Memoize the merged theme on the baseline + override references so unrelated store writes
// (messages, response, mock) don't produce a new theme object and re-render theme subscribers.
let effectiveThemeCache: {
  baseline: ProgramTheme;
  override: DeepPartial<ProgramTheme>;
  result: ProgramTheme;
} | null = null;

/** Preview overrides deep-merged onto the baseline (overrides win); the baseline when none are set. */
export const selectEffectiveProgramTheme = (s: ProgramStoreState): ProgramTheme | undefined => {
  const { baseline } = s;
  if (!baseline) return undefined;
  const override = s.overrides.programTheme;
  if (!override || Object.keys(override).length === 0) return baseline;
  if (
    effectiveThemeCache &&
    effectiveThemeCache.baseline === baseline &&
    effectiveThemeCache.override === override
  ) {
    return effectiveThemeCache.result;
  }
  const result = deepMergeReplacingArrays(baseline, override);
  effectiveThemeCache = { baseline, override, result };
  return result;
};

export const selectEffectiveThemeChoices = (s: ProgramStoreState) =>
  selectEffectiveProgramTheme(s)?.themeChoices;

export const selectEffectiveConfig = (s: ProgramStoreState) =>
  selectEffectiveProgramTheme(s)?.config;

export const selectEffectiveCustomCss = (s: ProgramStoreState): string | undefined =>
  selectEffectiveProgramTheme(s)?.customCss;

export const selectPreviewMessages = (s: ProgramStoreState) => s.overrides.messages;

export const selectResponseOverride = (
  s: ProgramStoreState,
): Partial<AllResponseTypes> | undefined => s.overrides.response;
